import React, { useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import CurrentSubscription from './CurrentSubscription';
import ChangePreferences from './ChangePreferences';
import AccountLinking from './AccountLinking';
import SubscriptionsDigitaliada from '../Subscriptions/SubscriptionsDigitaliada';
import { getSubscriptionData } from '../../shared/Capabilities';
import { cx } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS } from '../../constants';

const TABS = [
    { key: 'subscription', labelId: 'profile.tab.subscription.short', Panel: CurrentSubscription },
    { key: 'preferences', labelId: 'profile.tab.preferences.short', Panel: ChangePreferences },
    { key: 'digitaliada', labelId: 'profile.tab.digitaliada.short', Panel: AccountLinking },
];

const useStyles = makeStyles(() => ({
    head: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '18px 24px',
        marginBottom: '26px',
    },
    avatar: {
        width: '84px',
        height: '84px',
        fontSize: '2rem',
        border: `4px solid ${REDESIGN_COLORS.white}`,
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.dark,
        boxShadow: '0 12px 32px rgba(6, 63, 69, 0.12)',
    },
    who: {
        minWidth: 0,
        flex: 1,
    },
    eyebrowRow: {
        marginBottom: '10px',
    },
    name: {
        margin: '0 0 4px',
        fontSize: 'clamp(1.6rem, 3vw, 2.3rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    email: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        overflowWrap: 'anywhere',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '10px',
    },
    chip: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    },
    chipPlan: {
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
    },
    chipGuest: {
        color: REDESIGN_COLORS.accent,
        background: REDESIGN_TINTS.peach,
    },
    // The panel switcher, on the pricing section's segmented toggle.
    track: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: '2px',
        padding: '4px',
        marginBottom: '18px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
    },
    tab: {
        minHeight: '38px',
        padding: '0 18px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        cursor: 'pointer',
    },
    tabOn: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 3px rgba(6, 63, 69, 0.12)',
    },
}));

/**
 * The account page: who is signed in, then the subscription, the email
 * preferences and the Digitaliada link as switchable panels. Guests only get
 * the subscription panel; Digitaliada builds get their own subscription view.
 */
const Profile = ({ profile, subscriptions }) => {
    const classes = useStyles();
    const [active, setActive] = useState(TABS[0].key);

    const planName =
        subscriptions && profile.subscription ? getSubscriptionData(subscriptions, profile.subscription).subscriptionName : null;
    const tabs = profile.isGuest ? TABS.slice(0, 1) : TABS;
    const current = tabs.find((tab) => tab.key === active) || tabs[0];
    const Panel = current.Panel;

    return (
        <Grid item xs={12}>
            <LandingSection id="top" background="none" gutter={0} paddingTop={8} paddingBottom={16}>
                <div className={classes.head}>
                    <Avatar src={profile.avatar || undefined} alt="" className={classes.avatar}>
                        {(profile.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <div className={classes.who}>
                        <div className={classes.eyebrowRow}>
                            <SectionEyebrow>
                                <FormattedMessage id="profile.title" />
                            </SectionEyebrow>
                        </div>
                        <h1 className={classes.name}>{profile.name}</h1>
                        {!profile.isGuest ? <p className={classes.email}>{profile.email}</p> : null}
                        <div className={classes.chips}>
                            {planName ? <span className={cx(classes.chip, classes.chipPlan)}>{planName}</span> : null}
                            {profile.isGuest ? (
                                <span className={cx(classes.chip, classes.chipGuest)}>
                                    <FormattedMessage id="redesign.profile.guestChip" />
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </LandingSection>

            {window.isTeams ? null : (
                <LandingSection background="none" gutter={0} paddingTop={0} paddingBottom={56}>
                    {window.disableTracking ? (
                        <SubscriptionsDigitaliada />
                    ) : (
                        <>
                            {tabs.length > 1 ? (
                                <div className={classes.track} role="tablist">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            role="tab"
                                            aria-selected={tab.key === current.key}
                                            onClick={() => setActive(tab.key)}
                                            className={cx(classes.tab, tab.key === current.key && classes.tabOn)}
                                        >
                                            <FormattedMessage id={tab.labelId} />
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            <Panel />
                        </>
                    )}
                </LandingSection>
            )}
        </Grid>
    );
};

const mapStateToProps = (state) => {
    const { profile, mainAppState } = state;
    return { profile, subscriptions: mainAppState.subscriptions };
};

export default connect(mapStateToProps)(Profile);
